import React, { useCallback, useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Alert, Button, Divider, Spin, Typography } from 'antd';
import { DownOutlined, UpOutlined, ReloadOutlined } from '@ant-design/icons';
import { AUTO_TAG } from 'shared/constants/permissions';
import { JobStatus } from 'shared/prisma/types';
import Brain from 'components/Icons/Brain';
import LicenseTag from 'components/LicenseTag';
import SegmentTag from 'components/SegmentTag';
import useDataSource from 'hooks/useDataSource';
import useUser from 'hooks/useUser';
import useExternalAIConfirmation from 'hooks/useExternalAIConfirmation';
import messages from './messages';

const SUMMARY_FIELDS = `
  id
  createdAt
  updatedAt
  status
  language
  text
  state
  eligibleCount
  matchingCount
  capped
  totalMatching
  disabledReason
  stale
  canRegenerate
  currentEligibleCount
  scopeLabel
  temporary
`;

const GET_SUMMARY = gql`
  query TextResponseSummary($questionId: ID!, $questionResponseIds: [ID!]!, $reportSnapshot: TextResponseSummaryReportInput, $resultScope: String, $previewId: String) {
    textResponseSummary(questionId: $questionId, questionResponseIds: $questionResponseIds, reportSnapshot: $reportSnapshot, resultScope: $resultScope, previewId: $previewId) {
      ${SUMMARY_FIELDS}
    }
  }
`;

const GENERATE_SUMMARY = gql`
  mutation GenerateTextResponseSummary($input: GenerateTextResponseSummaryInput!) {
    generateTextResponseSummary(input: $input) {
      ${SUMMARY_FIELDS}
    }
  }
`;

const disabledMessage = {
  LICENSE: messages.summaryDisabledLicense,
  PII_SERVICE: messages.summaryDisabledPii,
  ALLOW_PERSONAL_DATA: messages.summaryDisabledPersonalData,
};

const boxStyle = { marginBottom: 16 };

const SummaryText = ({ text }) => {
  const blocks = [];
  let block;
  const renderInline = value => value.split(/(\*\*[^*\n]+\*\*)/g).map((part, index) => (
    index % 2 ? <strong key={index}>{part.slice(2, -2)}</strong> : part
  ));
  // ponytail: render flat lists, paragraphs and bold text; use a Markdown parser for nested lists or other formatting.
  text.split(/\r?\n/).forEach(line => {
    if (!line.trim()) { block = null; return; }
    const item = line.match(/^\s*(?:([-*+•])|(\d+)[.)])\s+(.+)$/);
    let type = 'paragraph';
    if (item) type = item[1] ? 'ul' : 'ol';
    if (!block || block.type !== type) {
      block = { type, lines: [], start: item?.[2] ? Number(item[2]) : undefined };
      blocks.push(block);
    }
    block.lines.push(item ? item[3] : line);
  });
  return blocks.map((part, index) => {
    const style = { marginBottom: 0, marginTop: index ? 8 : 0 };
    if (part.type === 'paragraph') {
      return <Typography.Paragraph key={index} style={{ ...style, whiteSpace: 'pre-wrap' }}>{renderInline(part.lines.join('\n'))}</Typography.Paragraph>;
    }
    return React.createElement(part.type, { key: index, start: part.start, style }, part.lines.map((line, i) => <li key={i}>{renderInline(line)}</li>));
  });
};

const TextResponseSummary = ({
  questionId,
  questionResponseIds,
  isReport,
  reportSnapshot,
  label,
  segment,
  resultScope = 'default:0',
  onSummaryState,
  previewId,
  dataSourceId,
}) => {
  const { hasDataSourcePermission, getDataSourceLicenseSetting, aiDisabled } = useDataSource({ dataSourceId, skip: isReport && !previewId, fetchPolicy: 'cache-and-network' });
  const { isSuperUser } = useUser();
  const confirmExternalAI = useExternalAIConfirmation();
  const summariesEnabled = isSuperUser || getDataSourceLicenseSetting('aiSummaries') === true;
  const canGenerate = summariesEnabled && hasDataSourcePermission(AUTO_TAG);
  const interactive = !isReport || Boolean(previewId);

  const { data, error, fetching, refetch, startPolling, stopPolling } = useQuery(GET_SUMMARY, {
    variables: { questionId, questionResponseIds, reportSnapshot, resultScope, previewId },
    fetchPolicy: 'network-only',
    skip: aiDisabled || !questionId || (isReport ? !reportSnapshot && !previewId : !summariesEnabled),
  });
  const [generateSummary, { loading: generating }] = useMutation(GENERATE_SUMMARY);

  const summary = data?.textResponseSummary;
  const inFlight = summary?.status === JobStatus.Pending || summary?.status === JobStatus.Running
    || summary?.state === 'PENDING' || summary?.state === 'RUNNING';

  useEffect(() => {
    if (error || summary || !fetching) {
      onSummaryState?.(resultScope, summary?.text ? 'saved' : (inFlight || (interactive && (error || (summary && summary.state !== 'EMPTY'))) ? 'available' : 'empty'));
    }
  }, [error, summary, fetching, resultScope, onSummaryState, interactive, inFlight]);

  useEffect(() => {
    if (inFlight) {
      startPolling(5000);
    } else {
      stopPolling();
    }
  }, [inFlight, startPolling, stopPolling]);

  if (aiDisabled || !questionId) {
    return null;
  }

  const runGenerate = async () => {
    if (!await confirmExternalAI()) return;
    await generateSummary({ variables: { input: { questionId, questionResponseIds, resultScope, scopeLabel: label, previewId, reportSnapshot, regenerate: Boolean(summary?.text) } } });
    await refetch();
  };

  const displayedLabel = summary?.text ? summary.scopeLabel || label?.split('\n')[0] : label;
  const segmentTitle = displayedLabel?.split('\n')[0];
  const displayedSegment = segment && displayedLabel ? (
    <>
      <SegmentTag segment={{ ...segment, id: segment.segmentId, title: segmentTitle }} />
      {displayedLabel.slice(segmentTitle.length).replace(/\n/g, ' ')}
    </>
  ) : displayedLabel?.replace(/\n/g, ' ');
  const scopeLabel = displayedLabel && (
    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
      <FormattedMessage {...messages.summaryScope} values={{ segment: displayedSegment, count: summary?.eligibleCount || 0 }} />
    </Typography.Paragraph>
  );

  if (!interactive && inFlight) return <Spin size="small" />;
  if (!interactive && summary?.state === 'FAILED') return <Alert type="warning" showIcon message={<FormattedMessage {...messages.summaryFailed} />} />;
  if (!interactive && !(summary?.state === 'COMPLETED' && summary.text)) return null;

  if (interactive && !summariesEnabled && (!isReport || !summary?.text)) {
    return <div style={boxStyle}><LicenseTag setting="aiSummaries" showTooltip /></div>;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        style={boxStyle}
        message={<FormattedMessage {...messages.summaryLoadFailed} />}
        action={(
          <Button onClick={() => refetch()} loading={fetching} size="small">
            <FormattedMessage {...messages.retrySummary} />
          </Button>
        )}
      />
    );
  }

  if (!summary || (!summary.text && summary.state === 'EMPTY')) return null;

  const actions = interactive && (!isReport || !summary.text || summary.temporary) && canGenerate && !summary.disabledReason && (summary.text || inFlight || ['READY', 'FAILED', 'COMPLETED'].includes(summary.state)) ? (
    <Button
      icon={<ReloadOutlined />}
      loading={generating || inFlight}
      disabled={inFlight || summary.canRegenerate === false}
      onClick={runGenerate}
      size="small"
    >
      <FormattedMessage {...(summary.text ? messages.regenerateSummary : messages.generateSummary)} />
    </Button>
  ) : null;
  const statusMessage = summary.disabledReason
    ? disabledMessage[summary.disabledReason] || messages.summaryDisabledPii
    : summary.state === 'LOW_VOLUME' || (summary.stale && summary.canRegenerate === false)
      ? messages.summaryLowVolume
      : summary.state === 'NOTHING_ELIGIBLE' ? messages.summaryNothingEligible
        : summary.state === 'FAILED' ? messages.summaryFailed : null;

  return (
    <section style={boxStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        {scopeLabel}
        {actions}
      </div>
      {!isReport && summary.stale && !generating && !inFlight && (
        <Alert type="warning" showIcon message={<FormattedMessage {...messages.summaryStale} />} style={{ marginBottom: 12 }} />
      )}
      {summary.text && <SummaryText text={summary.text} />}
      {isReport && previewId && (!summary.text || summary.temporary) && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0, marginTop: 8 }}>
          <FormattedMessage {...messages.summaryTemporary} />
        </Typography.Paragraph>
      )}
      {summary.text && (summary.updatedAt || summary.createdAt) && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0, marginTop: 8 }}>
          <FormattedMessage
            {...messages.summaryGeneratedAt}
            values={{ date: <FormattedDate value={summary.updatedAt || summary.createdAt} year="numeric" month="short" day="numeric" hour="2-digit" minute="2-digit" timeZone="Europe/Stockholm" /> }}
          />
        </Typography.Paragraph>
      )}
      {interactive && statusMessage && (
        <Typography.Paragraph type={summary.state === 'FAILED' ? 'danger' : 'secondary'}>
          <FormattedMessage {...statusMessage} values={{ count: summary.currentEligibleCount ?? summary.eligibleCount }} />
        </Typography.Paragraph>
      )}
      {summary.capped && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
          <FormattedMessage {...messages.summaryCapped} values={{ limit: 1000, total: summary.totalMatching }} />
        </Typography.Paragraph>
      )}
    </section>
  );
};

export default TextResponseSummary;

export const TextResponseSummaries = ({ summaries, isReport }) => {
  const [expanded, setExpanded] = useState(false);
  const [states, setStates] = useState({});
  const onSummaryState = useCallback((key, state) => {
    setStates(current => (current[key] === state ? current : { ...current, [key]: state }));
  }, []);
  const hasSavedSummary = summaries.some(({ resultScope }) => states[resultScope] === 'saved');
  const available = summaries.some(({ resultScope, questionResponseIds }) => states[resultScope] === 'available'
    || (states[resultScope] === undefined && questionResponseIds.length));
  const showSegments = isReport || hasSavedSummary || expanded;
  const showHeader = hasSavedSummary || available;
  const firstVisibleScope = summaries.find(summary => ['saved', 'available'].includes(states[summary.resultScope]))?.resultScope;

  return (
    <div style={showHeader ? { ...boxStyle, border: '1px solid #91caff', borderRadius: 6, background: '#e6f4ff' } : undefined}>
      {showHeader && (
        <Alert
          type="info"
          showIcon
          icon={<Brain />}
          style={{ border: 0, background: 'transparent' }}
          message={<FormattedMessage {...messages.aiGeneratedSummary} />}
          action={!isReport && !hasSavedSummary && (
            <Button size="small" icon={expanded ? <UpOutlined /> : <DownOutlined />} aria-expanded={expanded} onClick={() => setExpanded(current => !current)}>
              <FormattedMessage {...(expanded ? messages.hideSummarySegments : messages.showSummarySegments)} />
            </Button>
          )}
        />
      )}
      <div hidden={!showSegments} style={showHeader && showSegments ? { padding: '0 12px' } : undefined}>
        {summaries.map(({ key, ...props }) => (
          <React.Fragment key={key}>
            {['saved', 'available'].includes(states[props.resultScope]) && props.resultScope !== firstVisibleScope && <Divider style={{ margin: '12px 0' }} />}
            <TextResponseSummary {...props} isReport={isReport} onSummaryState={onSummaryState} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
